import { CONTEXT_2025_PROMO_CAMPAIGN_END, CONTEXT_2025_PROMO_CAMPAIGN_START } from "constants/campaigns";
import { IAgentFlags } from "store/entities/agents";

import { useAgentFlag } from "./use-agent-flag";

export interface ILimitedTimeCampaign {
  startDate: Date;
  endDate: Date;
  flag?: keyof IAgentFlags;
  additionalInfo?: {
    [key: string]: string | number | boolean;
  };
}

export const campaigns = {
  context2024: {
    startDate: new Date("2024-09-16T00:00:00.000Z"), // 2 weeks before the event
    endDate: new Date("2024-09-30T15:00:00.000Z"), // 11AM EDT, start of the event
    flag: "isContext2024BannerDisabled", // internal flag - we'll be able to disable the banner for users that REALLY don't want to see it anymore
    additionalInfo: {
      eventUrl:
        "https://text.com/context?utm_source=helpdesk&utm_medium=app&utm_campaign=launch_context24&utm_content=top_banner",
    },
  },
  context2025PromoModal: {
    startDate: new Date(CONTEXT_2025_PROMO_CAMPAIGN_START),
    endDate: new Date(CONTEXT_2025_PROMO_CAMPAIGN_END),
    flag: "context2025PromoModalClosedDate",
  },
  context2025PromoTopBarAlert: {
    startDate: new Date(CONTEXT_2025_PROMO_CAMPAIGN_START),
    endDate: new Date(CONTEXT_2025_PROMO_CAMPAIGN_END),
  },
} satisfies Record<string, ILimitedTimeCampaign>;

/**
 * useLimitedTimeCampaign is a hook that checks if a campaign is active.
 *
 * Based on the start date, end date and (optionally) an agent flag, returns whether or not a campaign is active (note that 'true' means the campaign is *inactive*).
 * When the flag doesn't exist, it's assumed to be false (as in 'the user has not closed the promo'). This is useful if you don't
 * need to preserve info about the user closing the promo.
 *
 * @param campaign - The campaign to check
 * @returns An object with the following properties:
 * - isCampaignActive: boolean - Whether the campaign is active
 * - setCampaignFlagValue: (value: boolean | string) => void - A function to set the flag value (noop if the flag is not provided)
 */
export const useLimitedTimeCampaign = (
  campaign: ILimitedTimeCampaign
): {
  isCampaignActive: boolean;
  setCampaignFlagValue: (value: boolean | string) => void;
} => {
  const { startDate, endDate, flag } = campaign;
  const [flagValue, setFlagValue] = useAgentFlag(flag);
  const dateNow = new Date();

  const isCampaignActive = dateNow >= startDate && dateNow <= endDate && !flagValue;
  return { isCampaignActive, setCampaignFlagValue: setFlagValue };
};
